import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  type Icon,
} from "@phosphor-icons/react";

export type StatBreakdownItem = {
  /** Also what a screen reader says in front of the figure. */
  label: string;
  icon: Icon;
  value: React.ReactNode;
};

/**
 * One key figure: what it counts, the number, and optionally how it splits
 * (per channel on the dashboard) and how it moved.
 *
 * The dashboard drew four of these by hand, each a `Card` with a shrunken
 * `CardTitle` and a `text-2xl` figure; the breakdown was one run of text, so
 * a long currency wrapped between its icon and its amount ("✉" on one line,
 * "0 €" on the next). Each item of the breakdown is now one unbreakable unit,
 * and the row wraps between items only.
 */
export function StatCard({
  label,
  value,
  breakdown,
  delta,
  loading = false,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  breakdown?: StatBreakdownItem[];
  /** A change, in percent; `label` says against what ("vs. last month"). */
  delta?: { value: number; label?: React.ReactNode };
  loading?: boolean;
  className?: string;
}) {
  return (
    <Card className={cn("flex flex-col gap-2 p-5 md:p-5", className)}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {loading ? (
        <>
          <Skeleton className="h-8 w-28" />
          {breakdown && <Skeleton className="h-4 w-full max-w-40" />}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-2xl font-semibold tabular-nums tracking-tight sm:text-3xl">
              {value}
            </span>
            {delta && <StatDelta {...delta} />}
          </div>
          {breakdown && breakdown.length > 0 && (
            <ul className="mt-auto flex flex-wrap gap-x-3 gap-y-1 pt-1 text-xs font-medium text-muted-foreground">
              {breakdown.map(({ label, icon: ItemIcon, value }) => (
                <li
                  key={label}
                  title={label}
                  className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums"
                >
                  <ItemIcon weight="fill" className="size-3.5" aria-hidden />
                  <span className="sr-only">{label}</span>
                  {value}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

function StatDelta({
  value,
  label,
}: {
  value: number;
  label?: React.ReactNode;
}) {
  const up = value >= 0;
  const DeltaIcon = up ? ArrowUpRightIcon : ArrowDownRightIcon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium",
        up ? "text-success" : "text-danger",
      )}
    >
      <DeltaIcon weight="bold" className="size-3" aria-hidden />
      {Math.abs(Math.round(value))}%
      {label && (
        <span className="ml-1 font-normal text-muted-foreground">{label}</span>
      )}
    </span>
  );
}
